import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import QuestionScreen from "./QuestionScreen";
import CompletionScreen from "./CompletionScreen";
import { QUESTIONS, INITIAL_ANSWERS } from "../lib/questions";
import { calculateSetupProfileResults } from "../lib/scoring";
import { clearState } from "../lib/storage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GATEWAY_URL = "https://nombaseconnect.com";

const PHASE = {
  QUESTION: "question",
  COMPLETE: "complete",
};

export default function SetupProfile() {
  const [loading, setLoading] = useState(true);
  const [sso, setSso] = useState(null);
  const [phase, setPhase] = useState(PHASE.QUESTION);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [toolsExtra, setToolsExtra] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submittedRef = useRef(false);
  const hydratedRef = useRef(false);
  const saveTimer = useRef(null);

  // Hydrate STRICTLY from the server, keyed on the verified SSO session (clerk_user_id).
  // No browser persistence — any pre-existing localStorage draft is purged so a
  // previous user's progress can never leak into a new user on the same browser.
  useEffect(() => {
    let active = true;
    clearState(); // purge legacy/leaked browser-persisted progress

    (async () => {
      try {
        const s = await axios.get(`${API}/intake/session`, { withCredentials: true });
        if (!active) return;
        if (!s.data || !s.data.is_sso_session) {
          window.location.replace(GATEWAY_URL);
          return;
        }
        // Already completed (or invited RBAC member) → never show the form.
        if (s.data.already_complete) {
          window.location.replace(GATEWAY_URL);
          return;
        }
        setSso(s.data);

        // Load THIS user's own server-side draft (resume case). Empty → start at Q1.
        // A transient draft-fetch failure must NOT boot an authenticated user — the
        // session is already confirmed valid, so we just start with an empty form.
        try {
          const d = await axios.get(`${API}/intake/draft`, { withCredentials: true });
          if (!active) return;
          const draft = d.data || {};
          setAnswers({ ...INITIAL_ANSWERS, ...(draft.answers || {}) });
          setToolsExtra(Array.isArray(draft.tools_extra) ? draft.tools_extra : []);
          setStepIndex(typeof draft.step_index === "number" ? draft.step_index : 0);
        } catch {
          // keep INITIAL_ANSWERS; user starts at Q1 rather than being logged out
        }
        if (!active) return;
        hydratedRef.current = true;
        setLoading(false);
      } catch {
        // The lockdown middleware 302s missing/expired sessions to the Gateway,
        // which surfaces here as a CORS/network error → bounce (no valid session).
        if (active) window.location.replace(GATEWAY_URL);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // Debounced per-user draft autosave (server-side, keyed on clerk_user_id by the cookie).
  useEffect(() => {
    if (!hydratedRef.current || submittedRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios
        .put(
          `${API}/intake/draft`,
          { answers, tools_extra: toolsExtra, step_index: stepIndex, phase },
          { withCredentials: true }
        )
        .catch(() => {});
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, toolsExtra, stepIndex, phase]);

  const currentQuestion = QUESTIONS[stepIndex];
  const value = currentQuestion ? answers[currentQuestion.id] : undefined;
  const otherValue =
    currentQuestion && currentQuestion.otherField ? answers[currentQuestion.otherField] : "";

  const canContinue = useMemo(() => {
    if (!currentQuestion) return false;
    if (currentQuestion.searchable) {
      const v = Array.isArray(value) ? value : [];
      return v.length > 0 || toolsExtra.length > 0;
    }
    if (currentQuestion.type === "multi") {
      const v = Array.isArray(value) ? value : [];
      if (v.length === 0) return false;
      if (v.includes("Other") && (!otherValue || !otherValue.trim())) return false;
      return true;
    }
    if (!value) return false;
    if (value === "Other" && (!otherValue || !otherValue.trim())) return false;
    return true;
  }, [currentQuestion, value, otherValue, toolsExtra]);

  const handleChange = (newVal) => {
    setAnswers((prev) => {
      const next = { ...prev, [currentQuestion.id]: newVal };
      // Q6 cross-clearing: selecting "None" removes added custom tools.
      if (currentQuestion.id === "current_tools" && Array.isArray(newVal) && newVal.includes("None")) {
        setToolsExtra([]);
      }
      // Clear the "*_other" field when "Other" is no longer selected.
      if (currentQuestion.otherField) {
        const hasOther = Array.isArray(newVal) ? newVal.includes("Other") : newVal === "Other";
        if (!hasOther) next[currentQuestion.otherField] = "";
      }
      return next;
    });
  };

  const handleOtherChange = (newVal) =>
    setAnswers((prev) => ({ ...prev, [currentQuestion.otherField]: newVal }));

  const handleToolsExtraChange = (next) => {
    if (next.length > 0) {
      setAnswers((prev) => ({
        ...prev,
        current_tools: (Array.isArray(prev.current_tools) ? prev.current_tools : []).filter((t) => t !== "None"),
      }));
    }
    setToolsExtra(next);
  };

  const handleBack = () => {
    if (stepIndex === 0) return;
    setStepIndex((i) => i - 1);
  };

  // After Q12 → submit immediately (no summary/review). No additional screens.
  const finalizeAndSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const mergedTools = Array.from(
      new Set(
        [...(Array.isArray(answers.current_tools) ? answers.current_tools : []), ...toolsExtra]
          .filter((t) => t && String(t).trim())
      )
    );
    const finalAnswers = { ...answers, current_tools: mergedTools, additional_context: "" };
    const results = calculateSetupProfileResults(finalAnswers);

    try {
      const r = await axios.post(
        `${API}/intake/complete`,
        { ...finalAnswers, ...results },
        { withCredentials: true }
      );
      if (r.data && r.data.redirect) {
        window.location.assign(r.data.redirect);
        return;
      }
    } catch (err) {
      console.error("Intake completion failed:", err);
    }
    submittedRef.current = false;
    setSubmitting(false);
    setPhase(PHASE.COMPLETE);
  };

  const handleContinue = () => {
    if (stepIndex < QUESTIONS.length - 1) {
      setStepIndex((i) => i + 1);
      return;
    }
    finalizeAndSubmit();
  };

  const handleRestart = () => {
    submittedRef.current = false;
    setAnswers(INITIAL_ANSWERS);
    setToolsExtra([]);
    setStepIndex(0);
    setSubmitting(false);
    setPhase(PHASE.QUESTION);
  };

  const isLast = stepIndex === QUESTIONS.length - 1;

  return (
    <div className="gw-page gw-grain">
      {loading ? (
        <div className="gw-small" data-testid="session-checking" style={{ color: "var(--text-secondary)" }}>
          Loading…
        </div>
      ) : submitting ? (
        <div className="gw-small" data-testid="submitting-loader" style={{ color: "var(--text-secondary)" }}>
          Submitting…
        </div>
      ) : (
        <>
          {sso && phase !== PHASE.COMPLETE && (
            <div
              data-testid="sso-identity-banner"
              className="gw-small"
              style={{
                position: "fixed",
                top: 16,
                left: "50%",
                transform: "translateX(-50%)",
                maxWidth: "min(92vw, 480px)",
                padding: "8px 16px",
                border: "1px solid var(--border-card)",
                borderRadius: 999,
                background: "var(--card)",
                color: "var(--text-secondary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                zIndex: 50,
              }}
            >
              <span>Signed in as </span>
              <span style={{ color: "var(--text-primary)" }}>{sso.name || sso.email}</span>
              {sso.organization_name ? ` · ${sso.organization_name}` : ""}
            </div>
          )}

          {phase === PHASE.QUESTION && currentQuestion && (
            <QuestionScreen
              question={currentQuestion}
              stepIndex={stepIndex}
              totalSteps={QUESTIONS.length}
              value={value}
              otherValue={otherValue}
              toolsOtherList={currentQuestion.searchable ? toolsExtra : undefined}
              onChange={handleChange}
              onOtherChange={handleOtherChange}
              onToolsOtherListChange={handleToolsExtraChange}
              onBack={handleBack}
              onContinue={handleContinue}
              canGoBack={stepIndex > 0}
              canContinue={canContinue}
              continueLabel={isLast ? "Submit" : "Continue"}
            />
          )}

          {phase === PHASE.COMPLETE && <CompletionScreen onRestart={handleRestart} />}
        </>
      )}
    </div>
  );
}
